import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LightBulbIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { suggestionService } from '../../services/suggestionService';
import { Suggestion } from '../../types/firestore';
import { Alert, Badge, Button, Card, Spinner } from '../../components/common/UIComponents';
import {
  SuggestionCard,
  SuggestionDetailPanel,
} from '../../components/Suggestions/SuggestionWorkspace';
import {
  SuggestionBucket,
  SUGGESTION_BUCKETS,
  matchesSuggestionBucket,
} from '../../utils/suggestionBuckets';
import {
  getSuggestionWorkflowStage,
  type SuggestionViewMode,
} from '../../utils/suggestionWorkflow';
import {
  getSuggestionHostSelectionKey,
  getSuggestionIdentity,
  isSuggestionInVisibleSet,
  isQuerySelectionPendingOutsideVisible,
  resolveSuggestionHostSelection,
  type SuggestionHostSelectionState,
} from '../../utils/suggestionHostSelection';
import { getSuggestionActionErrorMessage } from '../../utils/suggestionActionErrors';
import { sortSuggestionsForHost } from '../../utils/suggestionOrdering';
import { notificationService } from '../../services/notificationService';

const DELETE_CONFIRM_TOKEN = 'USUN';
const DEFAULT_VIEW: SuggestionViewMode = 'technical';
const MOBILE_BUCKET_KEYS: SuggestionBucket[] = ['all', 'pre_vote', 'voting', 'history'];

type AdminActionMode = 'delete' | null;

const AdminSuggestionsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<SuggestionBucket>('all');
  const [selection, setSelection] = useState<SuggestionHostSelectionState>({
    suggestionId: '',
    view: DEFAULT_VIEW,
  });
  const [busySuggestionId, setBusySuggestionId] = useState<string | null>(null);
  const [technicalTitleDraft, setTechnicalTitleDraft] = useState('');
  const [technicalSummaryDraft, setTechnicalSummaryDraft] = useState('');
  const [legacyResponseDraft, setLegacyResponseDraft] = useState('');
  const [declineReasonDraft, setDeclineReasonDraft] = useState('');
  const [actionMode, setActionMode] = useState<AdminActionMode>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const lastAppliedQuerySelectionKeyRef = useRef('');
  const handledNotificationReadbacksRef = useRef<Set<string>>(new Set());
  const selectedSuggestionId = selection.suggestionId;
  const selectedView = selection.view;

  const queryState = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      suggestionId: params.get('suggestionId')?.trim() || '',
      suggestionView:
        params.get('suggestionView')?.trim() === 'original' ? 'original' : 'technical',
    } as const;
  }, [location.search]);

  const visibleSuggestions = useMemo(
    () => suggestions.filter((item) => matchesSuggestionBucket(item, filter)),
    [filter, suggestions]
  );
  const pendingQuerySelectionOutsideVisible = useMemo(() => {
    return isQuerySelectionPendingOutsideVisible({
      suggestions,
      visibleSuggestions,
      querySelection: {
        suggestionId: queryState.suggestionId,
        view: queryState.suggestionView,
      },
      lastAppliedQuerySelectionKey: lastAppliedQuerySelectionKeyRef.current,
      loading,
    });
  }, [
    loading,
    queryState.suggestionId,
    queryState.suggestionView,
    suggestions,
    visibleSuggestions,
  ]);

  const selectedVisibleSuggestion = useMemo(
    () =>
      visibleSuggestions.find((item) => getSuggestionIdentity(item) === selection.suggestionId) ||
      null,
    [selection.suggestionId, visibleSuggestions]
  );

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await suggestionService.getSuggestionsForAdmin();
      setSuggestions(sortSuggestionsForHost([...data.active, ...data.archived]));
    } catch (loadError: any) {
      console.error('Nie udało się pobrać sugestii admina:', loadError);
      const code = String(loadError?.code || '');
      if (code.includes('permission-denied')) {
        setError('Brak uprawnień administratora do odczytu sugestii.');
      } else {
        setError('Nie udało się pobrać sugestii.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSuggestions();
  }, [loadSuggestions]);

  useEffect(() => {
    if (pendingQuerySelectionOutsideVisible) {
      setFilter('all');
      return;
    }

    const querySelectionKey = getSuggestionHostSelectionKey({
      suggestionId: queryState.suggestionId,
      view: queryState.suggestionView,
    });
    const allowQuerySelection =
      Boolean(queryState.suggestionId) &&
      querySelectionKey !== lastAppliedQuerySelectionKeyRef.current &&
      isSuggestionInVisibleSet(visibleSuggestions, queryState.suggestionId);

    setSelection((currentSelection) => {
      const nextSelection = resolveSuggestionHostSelection({
        visibleSuggestions,
        currentSelection,
        querySelection: {
          suggestionId: queryState.suggestionId,
          view: queryState.suggestionView,
        },
        defaultView: DEFAULT_VIEW,
        allowQuerySelection,
      });

      if (allowQuerySelection && nextSelection.suggestionId === queryState.suggestionId) {
        lastAppliedQuerySelectionKeyRef.current = querySelectionKey;
      }

      if (
        nextSelection.suggestionId === currentSelection.suggestionId &&
        nextSelection.view === currentSelection.view
      ) {
        return currentSelection;
      }

      return nextSelection;
    });
  }, [
    pendingQuerySelectionOutsideVisible,
    queryState.suggestionId,
    queryState.suggestionView,
    visibleSuggestions,
  ]);

  useEffect(() => {
    if (!selectedVisibleSuggestion) {
      return;
    }

    setTechnicalTitleDraft(
      selectedVisibleSuggestion.technicalTitle ||
        selectedVisibleSuggestion.originalTitle ||
        selectedVisibleSuggestion.title ||
        ''
    );
    setTechnicalSummaryDraft(
      selectedVisibleSuggestion.technicalSummary ||
        selectedVisibleSuggestion.originalDescription ||
        selectedVisibleSuggestion.description ||
        ''
    );
    setLegacyResponseDraft(selectedVisibleSuggestion.response?.text || '');
    setDeclineReasonDraft(selectedVisibleSuggestion.adminDecisionReason || '');
    setActionMode(null);
    setDeleteConfirmText('');
  }, [selectedVisibleSuggestion]);

  useEffect(() => {
    if ((!selection.suggestionId && loading) || pendingQuerySelectionOutsideVisible) {
      return;
    }

    const params = new URLSearchParams(location.search);
    if (!selection.suggestionId) {
      if (!params.has('suggestionId') && !params.has('suggestionView')) {
        return;
      }

      params.delete('suggestionId');
      params.delete('suggestionView');
      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
      return;
    }

    if (
      params.get('suggestionId') === selection.suggestionId &&
      params.get('suggestionView') === selection.view
    ) {
      return;
    }

    params.set('suggestionId', selection.suggestionId);
    params.set('suggestionView', selection.view);
    lastAppliedQuerySelectionKeyRef.current = getSuggestionHostSelectionKey({
      suggestionId: selection.suggestionId,
      view: selection.view,
    });
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [
    loading,
    location.pathname,
    location.search,
    navigate,
    selection.suggestionId,
    selection.view,
    pendingQuerySelectionOutsideVisible,
  ]);

  useEffect(() => {
    const notificationId = new URLSearchParams(location.search).get('notificationId')?.trim() || '';
    if (
      loading ||
      Boolean(error) ||
      pendingQuerySelectionOutsideVisible ||
      !notificationId ||
      !queryState.suggestionId ||
      selection.suggestionId !== queryState.suggestionId ||
      selection.view !== queryState.suggestionView ||
      !selectedVisibleSuggestion
    ) {
      return;
    }

    const readbackKey = `${notificationId}:${queryState.suggestionId}:${queryState.suggestionView}`;
    if (handledNotificationReadbacksRef.current.has(readbackKey)) {
      return;
    }
    handledNotificationReadbacksRef.current.add(readbackKey);
    notificationService
      .markSuggestionNotificationAsRead({
        notificationId,
        suggestionId: queryState.suggestionId,
        suggestionView: queryState.suggestionView,
        host: 'admin',
      })
      .then((read) => {
        if (!read) {
          handledNotificationReadbacksRef.current.delete(readbackKey);
        }
      })
      .catch((readbackError) => {
        handledNotificationReadbacksRef.current.delete(readbackKey);
        console.error('Nie udało się potwierdzić odczytu powiadomienia sugestii:', readbackError);
      });
  }, [
    error,
    loading,
    location.search,
    pendingQuerySelectionOutsideVisible,
    queryState.suggestionId,
    queryState.suggestionView,
    selectedVisibleSuggestion,
    selection.suggestionId,
    selection.view,
  ]);

  const handleSelectSuggestion = (suggestionId: string) => {
    if (!suggestionId) {
      return;
    }
    setSelection((current) =>
      current.suggestionId === suggestionId
        ? { suggestionId: '', view: DEFAULT_VIEW }
        : {
            suggestionId,
            view: DEFAULT_VIEW,
          }
    );
  };

  const runAction = async (targetSuggestionId: string, action: () => Promise<unknown>) => {
    if (!targetSuggestionId) {
      return;
    }

    setBusySuggestionId(targetSuggestionId);
    setError(null);
    try {
      await action();
      await loadSuggestions();
    } catch (actionError: any) {
      console.error('Nie udało się wykonać akcji dla sugestii:', actionError);
      setError(getSuggestionActionErrorMessage(actionError));
    } finally {
      setBusySuggestionId(null);
    }
  };

  const handleSaveTechnical = async () => {
    if (!selectedSuggestionId) {
      return;
    }
    await runAction(selectedSuggestionId, () =>
      suggestionService.saveSuggestionTechnicalVersion({
        suggestionId: selectedSuggestionId,
        technicalTitle: technicalTitleDraft,
        technicalSummary: technicalSummaryDraft,
      })
    );
  };

  const handleDecline = async () => {
    if (!selectedSuggestionId) {
      return;
    }

    await runAction(selectedSuggestionId, () =>
      suggestionService.declineSuggestionByAdmin({
        suggestionId: selectedSuggestionId,
        reason: declineReasonDraft || 'Odrzucono przed głosowaniem.',
      })
    );
  };

  const handleOpenVoting = async () => {
    if (!selectedSuggestionId) {
      return;
    }
    await runAction(selectedSuggestionId, () =>
      suggestionService.openSuggestionVoting({ suggestionId: selectedSuggestionId })
    );
  };

  const handleCloseVoting = async () => {
    if (!selectedSuggestionId) {
      return;
    }
    await runAction(selectedSuggestionId, () =>
      suggestionService.closeSuggestionVotingIfEligible({ suggestionId: selectedSuggestionId })
    );
  };

  const handleImplementationState = async (
    nextStatus: 'planned' | 'in_progress' | 'implemented'
  ) => {
    if (!selectedSuggestionId) {
      return;
    }
    await runAction(selectedSuggestionId, () =>
      suggestionService.setSuggestionImplementationState({
        suggestionId: selectedSuggestionId,
        nextStatus,
      })
    );
  };

  const handleLegacyUpdate = async (status: 'accepted' | 'rejected') => {
    if (!selectedSuggestionId) {
      return;
    }
    await runAction(selectedSuggestionId, () =>
      suggestionService.updateSuggestionByAdmin(selectedSuggestionId, {
        status,
      })
    );
  };

  const handleArchiveToggle = async (shouldArchive: boolean) => {
    if (!selectedSuggestionId) {
      return;
    }
    await runAction(selectedSuggestionId, () =>
      suggestionService.setSuggestionArchivedByAdmin(selectedSuggestionId, {
        archived: shouldArchive,
      })
    );
  };

  const handleDeleteSuggestion = async () => {
    if (!selectedSuggestionId) {
      return;
    }

    if (deleteConfirmText !== DELETE_CONFIRM_TOKEN) {
      setError(`Wpisz token ${DELETE_CONFIRM_TOKEN}, aby trwale usunąć sugestię.`);
      return;
    }

    const confirmed = window.confirm(
      'Ta operacja jest nieodwracalna. Sugestia i subkolekcje zostaną trwale usunięte. Kontynuować?'
    );
    if (!confirmed) {
      return;
    }

    await runAction(selectedSuggestionId, () =>
      suggestionService.deleteSuggestionByAdmin(selectedSuggestionId)
    );
    setActionMode(null);
  };

  const selectedSuggestionStage = selectedVisibleSuggestion
    ? getSuggestionWorkflowStage(selectedVisibleSuggestion)
    : 'legacy';

  const canArchive =
    selectedVisibleSuggestion?.workflowVersion === 1 ||
    [
      'declined_by_admin',
      'voting_closed_accepted',
      'voting_closed_rejected',
      'planned',
      'in_progress',
      'implemented',
    ].includes(String(selectedVisibleSuggestion?.status || ''));
  const canRestore =
    selectedVisibleSuggestion?.status === 'archived' ||
    selectedVisibleSuggestion?.isArchived === true;
  const canDelete = canRestore;
  const canEditTechnical =
    selectedVisibleSuggestion?.workflowVersion === 2 &&
    ['new', 'admin_review'].includes(String(selectedVisibleSuggestion?.status || ''));
  const canDecline =
    selectedVisibleSuggestion?.workflowVersion === 2 &&
    ['new', 'admin_review'].includes(String(selectedVisibleSuggestion?.status || ''));
  const canOpenVoting =
    selectedVisibleSuggestion?.workflowVersion === 2 &&
    ['new', 'admin_review'].includes(String(selectedVisibleSuggestion?.status || ''));
  const canCloseVoting =
    selectedVisibleSuggestion?.workflowVersion === 2 &&
    selectedVisibleSuggestion?.status === 'voting_open';
  const canAdvanceImplementation =
    selectedVisibleSuggestion?.workflowVersion === 2 &&
    selectedVisibleSuggestion?.status === 'voting_closed_accepted';

  const canArchiveSuggestion = (suggestion: Suggestion): boolean =>
    suggestion.workflowVersion === 1 ||
    [
      'declined_by_admin',
      'voting_closed_accepted',
      'voting_closed_rejected',
      'planned',
      'in_progress',
      'implemented',
    ].includes(String(suggestion.status || ''));

  const groupCounts = useMemo(
    () =>
      SUGGESTION_BUCKETS.reduce<Record<SuggestionBucket, number>>(
        (acc, bucket) => {
          acc[bucket.key] =
            bucket.key === 'all'
              ? suggestions.length
              : suggestions.filter((item) => matchesSuggestionBucket(item, bucket.key)).length;
          return acc;
        },
        { all: 0, pre_vote: 0, voting: 0, post_vote: 0, implementation: 0, history: 0 }
      ),
    [suggestions]
  );
  const mobileBuckets = SUGGESTION_BUCKETS.filter((bucket) =>
    MOBILE_BUCKET_KEYS.includes(bucket.key)
  );
  const renderFilters = (buckets: typeof SUGGESTION_BUCKETS, className: string) => (
    <div className={className}>
      {buckets.map((bucket) => (
        <Button
          key={bucket.key}
          type="button"
          variant={filter === bucket.key ? 'primary' : 'neutral'}
          size="sm"
          onClick={() => setFilter(bucket.key)}
        >
          {bucket.label}
          <span className="ml-2 rounded-full bg-black/15 px-2 py-0.5 text-[11px]">
            {groupCounts[bucket.key]}
          </span>
        </Button>
      ))}
    </div>
  );

  const renderAdminActionArea = () => {
    if (!selectedVisibleSuggestion) {
      return null;
    }

    return (
      <div className="space-y-3">
        {selectedVisibleSuggestion.workflowVersion === 2 && (
          <>
            {canEditTechnical && (
              <section className="space-y-3 rounded-lg border border-primary-dark-300/60 bg-primary-dark-100/50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-primary-light-200">
                    Wersja techniczna
                  </h3>
                  <Badge variant="info" size="sm">
                    Edycja admina
                  </Badge>
                </div>
                <div className="space-y-3">
                  <label className="block text-sm text-primary-light-300">Tytuł techniczny</label>
                  <input
                    value={technicalTitleDraft}
                    onChange={(event) => setTechnicalTitleDraft(event.target.value)}
                    className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 py-2 text-sm text-primary-light-100 placeholder:text-primary-light-500"
                    placeholder="Tytuł po opracowaniu"
                  />
                  <label className="block text-sm text-primary-light-300">
                    Podsumowanie techniczne
                  </label>
                  <textarea
                    value={technicalSummaryDraft}
                    onChange={(event) => setTechnicalSummaryDraft(event.target.value)}
                    rows={5}
                    className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 py-2 text-sm text-primary-light-100 placeholder:text-primary-light-500"
                    placeholder="Jak admin przekłada oryginał na wersję techniczną..."
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => void handleSaveTechnical()}
                      disabled={busySuggestionId === selectedSuggestionId}
                    >
                      Zapisz wersję techniczną
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {canOpenVoting && (
              <section className="space-y-3 rounded-lg border border-primary-dark-300/60 bg-primary-dark-100/50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-primary-light-200">Start głosowania</h3>
                  <Badge variant="info" size="sm">
                    7 dni / snapshot
                  </Badge>
                </div>
                <p className="text-xs text-primary-light-400">
                  Otwarcie głosowania zapisuje snapshot elektoratu i blokuje edycję treści.
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="primary"
                    onClick={() => void handleOpenVoting()}
                    disabled={busySuggestionId === selectedSuggestionId}
                  >
                    Otwórz głosowanie
                  </Button>
                </div>
              </section>
            )}

            {selectedVisibleSuggestion.status === 'voting_open' && (
              <section className="space-y-3 rounded-lg border border-primary-dark-300/60 bg-primary-dark-100/50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-primary-light-200">
                    Zamknięcie głosowania
                  </h3>
                  <Badge variant="neutral" size="sm">
                    {selectedVisibleSuggestion.yesCount || 0} /{' '}
                    {selectedVisibleSuggestion.noCount || 0}
                  </Badge>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="neutral"
                    onClick={() => void handleCloseVoting()}
                    disabled={busySuggestionId === selectedSuggestionId || !canCloseVoting}
                  >
                    Zamknij głosowanie
                  </Button>
                </div>
              </section>
            )}

            {canDecline && (
              <section className="space-y-3 rounded-lg border border-primary-dark-300/60 bg-primary-dark-100/50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-primary-light-200">
                    Odrzuć przed głosowaniem
                  </h3>
                  <Badge variant="error" size="sm">
                    Pre-vote
                  </Badge>
                </div>
                <textarea
                  value={declineReasonDraft}
                  onChange={(event) => setDeclineReasonDraft(event.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 py-2 text-sm text-primary-light-100 placeholder:text-primary-light-500"
                  placeholder="Powód odmowy"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="error"
                    onClick={() => void handleDecline()}
                    disabled={busySuggestionId === selectedSuggestionId}
                  >
                    Odrzuć temat
                  </Button>
                </div>
              </section>
            )}

            {canAdvanceImplementation && (
              <section className="space-y-3 rounded-lg border border-primary-dark-300/60 bg-primary-dark-100/50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-primary-light-200">Etapy realizacji</h3>
                  <Badge variant="success" size="sm">
                    Pozytywny wynik
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="neutral"
                    size="sm"
                    onClick={() => void handleImplementationState('planned')}
                    disabled={busySuggestionId === selectedSuggestionId}
                  >
                    Planned
                  </Button>
                  <Button
                    type="button"
                    variant="neutral"
                    size="sm"
                    onClick={() => void handleImplementationState('in_progress')}
                    disabled={busySuggestionId === selectedSuggestionId}
                  >
                    In progress
                  </Button>
                  <Button
                    type="button"
                    variant="success"
                    size="sm"
                    onClick={() => void handleImplementationState('implemented')}
                    disabled={busySuggestionId === selectedSuggestionId}
                  >
                    Implemented
                  </Button>
                </div>
              </section>
            )}
          </>
        )}

        {selectedVisibleSuggestion.workflowVersion === 1 && (
          <section className="space-y-3 rounded-lg border border-primary-dark-300/60 bg-primary-dark-100/50 p-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-primary-light-200">Legacy moderacja</h3>
              <Badge variant="warning" size="sm">
                v1
              </Badge>
            </div>
            <textarea
              value={legacyResponseDraft}
              onChange={(event) => setLegacyResponseDraft(event.target.value)}
              rows={4}
              className="w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 py-2 text-sm text-primary-light-100 placeholder:text-primary-light-500"
              placeholder="Wpisz odpowiedź dla użytkownika"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="neutral"
                onClick={() =>
                  void runAction(selectedSuggestionId, () =>
                    suggestionService.updateSuggestionByAdmin(selectedSuggestionId, {
                      responseText: legacyResponseDraft,
                    })
                  )
                }
                disabled={busySuggestionId === selectedSuggestionId}
              >
                Zapisz odpowiedź
              </Button>
              <Button
                type="button"
                variant="success"
                onClick={() => void handleLegacyUpdate('accepted')}
                disabled={busySuggestionId === selectedSuggestionId}
              >
                Zaakceptuj
              </Button>
              <Button
                type="button"
                variant="error"
                onClick={() => void handleLegacyUpdate('rejected')}
                disabled={busySuggestionId === selectedSuggestionId}
              >
                Odrzuć
              </Button>
            </div>
          </section>
        )}

        <section className="space-y-3 rounded-lg border border-primary-dark-300/60 bg-primary-dark-100/50 p-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-primary-light-200">Porządkowanie</h3>
            <Badge variant="neutral" size="sm">
              {canDelete ? 'Archiwum' : 'Aktywna'}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="neutral"
              onClick={() =>
                void handleArchiveToggle(
                  !Boolean(
                    selectedVisibleSuggestion.isArchived ||
                      selectedVisibleSuggestion.status === 'archived'
                  )
                )
              }
              disabled={busySuggestionId === selectedSuggestionId || (!canArchive && !canRestore)}
            >
              {selectedVisibleSuggestion.isArchived ||
              selectedVisibleSuggestion.status === 'archived'
                ? 'Przywróć z archiwum'
                : 'Archiwizuj'}
            </Button>
            {canDelete && (
              <Button
                type="button"
                variant="error"
                onClick={() => setActionMode('delete')}
                disabled={busySuggestionId === selectedSuggestionId}
              >
                <TrashIcon className="mr-2 h-4 w-4" />
                Usuń trwale
              </Button>
            )}
          </div>

          {actionMode === 'delete' && canDelete && (
            <div className="space-y-3 rounded-xl border border-status-error-500/45 bg-status-error-500/10 p-3">
              <p className="text-sm font-semibold text-status-error-500">Operacja nieodwracalna</p>
              <p className="text-xs text-status-error-500/90">
                Aby potwierdzić trwałe usunięcie, wpisz token{' '}
                <span className="font-semibold">{DELETE_CONFIRM_TOKEN}</span>.
              </p>
              <input
                value={deleteConfirmText}
                onChange={(event) => setDeleteConfirmText(event.target.value.trim())}
                placeholder={`Wpisz ${DELETE_CONFIRM_TOKEN}`}
                className="w-full rounded-lg border border-status-error-500/60 bg-primary-dark-100 px-3 py-2 text-sm text-primary-light-100 placeholder:text-primary-light-500"
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="neutral"
                  size="sm"
                  onClick={() => {
                    setActionMode(null);
                    setDeleteConfirmText('');
                  }}
                >
                  Anuluj
                </Button>
                <Button
                  type="button"
                  variant="error"
                  size="sm"
                  onClick={() => void handleDeleteSuggestion()}
                  disabled={
                    deleteConfirmText !== DELETE_CONFIRM_TOKEN ||
                    busySuggestionId === selectedSuggestionId
                  }
                >
                  Potwierdź trwałe usunięcie
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>
    );
  };

  return (
    <div className="py-3 md:py-6">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-3.5 sm:gap-5">
        <header className="rs-brand-keyline relative rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-3 pl-4 pr-12 sm:p-5 sm:pl-5">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="absolute right-3 top-3 rounded-full border border-primary-dark-300 bg-primary-dark-100/90 p-2 text-primary-light-300 transition hover:text-primary-light-100"
            aria-label="Zamknij sugestie admina"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold-300/12 text-gold-300 sm:h-10 sm:w-10">
                  <LightBulbIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-primary-light-400 sm:text-xs sm:tracking-[0.22em]">
                    Admin sugestii
                  </p>
                  <h1 className="mt-0.5 max-w-2xl text-lg font-semibold leading-tight text-primary-light-100 sm:mt-1 sm:text-2xl">
                    Queue + detail dla workflow v2
                  </h1>
                </div>
              </div>
              <p className="max-w-2xl text-sm leading-5 text-primary-light-300 sm:leading-6">
                Operacyjna kolejka opracowania, głosowania i realizacji sugestii.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="neutral" size="sm">
                  {visibleSuggestions.length} widocznych
                </Badge>
                {selectedVisibleSuggestion && (
                  <Badge variant="neutral" size="sm">
                    Etap: {selectedSuggestionStage}
                  </Badge>
                )}
                {selectedVisibleSuggestion?.workflowVersion === 1 && (
                  <Badge variant="warning" size="sm">
                    Legacy v1
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </header>

        {renderFilters(
          mobileBuckets,
          'flex gap-2 overflow-x-auto rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-1.5 sm:hidden'
        )}
        {renderFilters(
          SUGGESTION_BUCKETS,
          'hidden flex-wrap gap-2 rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-1.5 sm:flex'
        )}

        {loading && (
          <div className="flex items-center justify-center rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-6">
            <Spinner size="lg" />
            <p className="ml-3 text-primary-light-300">Ładowanie sugestii...</p>
          </div>
        )}

        {error && <Alert variant="error">{error}</Alert>}

        {!loading && !error && suggestions.length === 0 && (
          <Card className="p-6">
            <p className="text-sm text-primary-light-300">Brak sugestii do obsługi.</p>
          </Card>
        )}

        {!loading && !error && suggestions.length > 0 && (
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)]">
            <section className="space-y-3">
              <div className="space-y-3">
                {visibleSuggestions.length === 0 ? (
                  <Card className="p-4">
                    <p className="text-sm text-primary-light-300">Brak sugestii w tym filtrze.</p>
                  </Card>
                ) : (
                  visibleSuggestions.map((suggestion) => {
                    const suggestionId = getSuggestionIdentity(suggestion);
                    const isSelected = suggestionId === selectedSuggestionId;
                    const expandedDetail = isSelected ? (
                      <div className="xl:hidden">
                        {busySuggestionId === selectedSuggestionId && (
                          <div className="mb-3 flex items-center rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/75 p-3">
                            <Spinner size="md" />
                            <p className="ml-3 text-sm text-primary-light-300">
                              Zapisywanie zmian...
                            </p>
                          </div>
                        )}
                        <SuggestionDetailPanel
                          suggestion={selectedVisibleSuggestion}
                          selectedView={selectedView}
                          onViewChange={(view) => setSelection((current) => ({ ...current, view }))}
                          showEligibilityNotice={false}
                          actionArea={selectedVisibleSuggestion ? renderAdminActionArea() : null}
                          embedded
                        />
                      </div>
                    ) : null;

                    return (
                      <div key={suggestionId} className="space-y-2">
                        <SuggestionCard
                          suggestion={suggestion}
                          selected={isSelected}
                          onSelect={handleSelectSuggestion}
                          activeView={isSelected ? selectedView : undefined}
                          showDeadline={suggestion.workflowVersion === 2}
                          expandedContent={expandedDetail}
                          inlineExpanded={isSelected}
                        />
                        <div className="hidden flex-wrap gap-2 px-1 xl:flex">
                          <Button
                            type="button"
                            size="sm"
                            variant={isSelected ? 'primary' : 'neutral'}
                            onClick={() => handleSelectSuggestion(suggestionId)}
                          >
                            Moderuj
                          </Button>
                          {suggestion.workflowVersion === 2 && canArchiveSuggestion(suggestion) && (
                            <Button
                              type="button"
                              size="sm"
                              variant="neutral"
                              onClick={() => {
                                handleSelectSuggestion(suggestionId);
                                void runAction(suggestionId, () =>
                                  suggestionService.setSuggestionArchivedByAdmin(suggestionId, {
                                    archived: true,
                                  })
                                );
                              }}
                              disabled={busySuggestionId === suggestionId}
                            >
                              Archiwizuj
                            </Button>
                          )}
                          {suggestion.workflowVersion === 1 && (
                            <Button
                              type="button"
                              size="sm"
                              variant={suggestion.isArchived ? 'success' : 'neutral'}
                              onClick={() => {
                                handleSelectSuggestion(suggestionId);
                                void runAction(suggestionId, () =>
                                  suggestionService.setSuggestionArchivedByAdmin(suggestionId, {
                                    archived: !suggestion.isArchived,
                                  })
                                );
                              }}
                              disabled={busySuggestionId === suggestionId}
                            >
                              {suggestion.isArchived ? 'Przywróć z archiwum' : 'Archiwizuj'}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            <aside className="hidden self-start space-y-3 xl:sticky xl:top-5 xl:block">
              {busySuggestionId === selectedSuggestionId && (
                <Card className="p-4">
                  <div className="flex items-center">
                    <Spinner size="md" />
                    <p className="ml-3 text-sm text-primary-light-300">Zapisywanie zmian...</p>
                  </div>
                </Card>
              )}

              <SuggestionDetailPanel
                suggestion={selectedVisibleSuggestion}
                selectedView={selectedView}
                onViewChange={(view) => setSelection((current) => ({ ...current, view }))}
                showEligibilityNotice={false}
                actionArea={renderAdminActionArea()}
              />
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSuggestionsPage;
